import React, { useState } from 'react';
import { useMount } from 'react-use';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Input,
  useMediaQuery, Box, Typography, Stack,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useStoriesQuery } from '@modules/Stories/hooks/useStoriesQuery';
import { StoryListItem } from '@modules/Meetings/partials/SelectStory/SelectStoryListItem';
import { useMeetingCrud } from '@modules/Meetings/hooks/useMeetingCrud';
import { useMeetingQuery } from '@modules/Meetings/hooks/useMeetingQuery';
import { useDebouncedState } from '@shared/hooks/useDebouncedState';
import { StoryTypeSelect } from '@modules/Stories/components/StoryTypeSelect';
import { LoadingButton } from '@mui/lab';




interface Props {
  open: boolean,
  onClose: () => void,
  options: {
    meetingId: number,
  };
}

export const LinkStoriesToMeetingDialog: React.FC<Props> = (props ) => {
  const [searchTerm, setSearchTerm, debouncedSearchTerm] = useDebouncedState('', 500);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const { open, onClose, options } = props;
  const { meetingId } = options || {};
  const { editMeeting } = useMeetingCrud();
  const { stories } = useStoriesQuery({
    search: debouncedSearchTerm,
    type: typeFilter !== null ? typeFilter as Stories.StoryType : undefined,
  });
  const { meeting } = useMeetingQuery({ id: meetingId }, { enabled: !!meetingId });

  const handleCancel = () => {
    onClose();
  };

  const [selectedStoryIds, setSelectedStoryIds] = useState<number[]>([]);

  useMount(() => {
    if (meeting && meeting.storiesThrough) {
      setSelectedStoryIds(meeting.storiesThrough.map(storyThrough => storyThrough.story.id));
    }
  });

  const handleStoryToggle = React.useCallback((storyId: number, checked: boolean) => {
    setSelectedStoryIds(prev =>
      checked ? [...prev, storyId] : prev.filter(id => id !== storyId),
    );
  }, []);

  const handleSave = async () => {
    await editMeeting.mutateAsync({
      id: meetingId,
      storiesThrough: selectedStoryIds.map(storyId => ({
        story: { id: storyId },
      })),
    });
    onClose();
  };

  if (!meetingId) {
    onClose();
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={isMobile}>
      <DialogTitle>Link Stories to {meeting?.title}</DialogTitle>
      <DialogContent>
        <Stack gap={2}>
          <Input
            placeholder="Search by title, type, or tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            startAdornment={<Search/>}
          />
          <StoryTypeSelect value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}/>
          <Box display="flex" flexDirection="column" gap={2}>
            {stories.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No stories found matching your filters.
              </Typography>
            )}
            {stories.map((story) => (
              <StoryListItem
                key={story.id}
                story={story}
                checked={selectedStoryIds.includes(story.id)}
                onToggle={handleStoryToggle}
              />
            ))}
          </Box>
        </Stack>
      </DialogContent>
      <Stack direction="row" justifyContent="flex-end" gap={1} sx={{ p: 2 }}>
        <Button variant="outlined" onClick={handleCancel}>Cancel</Button>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          loading={editMeeting.isPending}
        >
          Save Links
        </LoadingButton>
      </Stack>
    </Dialog>
  );
};
